import { ApplicationRef, EnvironmentInjector, Injectable, NgZone, createComponent, inject } from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ResumenVisitaPdfComponent, type ResumenVisitaPdfMeta } from '../components/resumen-visita-pdf/resumen-visita-pdf.component';
import { descargarOCompartirBlob } from '../utils/abrir-adjunto.util';
import { firstValueFrom } from 'rxjs';
import { VisitasService, type Visita } from './visitas.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class ResumenVisitaPdfService {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly toastService = inject(ToastService);
  private readonly visitasService = inject(VisitasService);

  async descargarResumenVisita(opts: { visita: Visita; meta?: ResumenVisitaPdfMeta; logoSrc?: string }): Promise<void> {
    const componentRef = createComponent(ResumenVisitaPdfComponent, {
      environmentInjector: this.envInjector
    });

    const [objetivosCatalogo, modalidadesCatalogo] = await Promise.all([
      firstValueFrom(this.visitasService.obtenerObjetivosVisita()),
      firstValueFrom(this.visitasService.obtenerModalidadContacto()),
    ]);

    componentRef.instance.visita = opts.visita;
    componentRef.instance.meta = opts.meta || { extensionista: '', fecha_emision: new Date().toISOString() };
    componentRef.instance.logoSrc = opts.logoSrc || 'assets/logo-indap.png';
    componentRef.instance.objetivosCatalogo = Array.isArray(objetivosCatalogo) ? objetivosCatalogo : [];
    componentRef.instance.modalidadesCatalogo = Array.isArray(modalidadesCatalogo) ? modalidadesCatalogo : [];

    this.appRef.attachView(componentRef.hostView);
    const hostElement = document.createElement('div');
    hostElement.style.position = 'absolute';
    hostElement.style.left = '-10000px';
    hostElement.style.top = '0';
    hostElement.style.width = '210mm';
    hostElement.style.backgroundColor = 'white';
    document.body.appendChild(hostElement);
    hostElement.appendChild(componentRef.location.nativeElement);

    try {
      await this.waitForStableDom(componentRef);

      const pdf = new jsPDF('p', 'mm', 'a4', true);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pdfWidth - (margin * 2);

      const container = hostElement.querySelector('.rv-pdf') as HTMLElement;
      const bigCanvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
      const bigCanvasWidth = bigCanvas.width;

      const getFragment = (selector: string, pT: number = 8, pB: number = 0): { imgData: string | null; heightMm: number } => {
        const el = container.querySelector(selector) as HTMLElement;
        if (!el || el.offsetHeight === 0) return { imgData: null, heightMm: 0 };
        const rect = el.getBoundingClientRect();
        const rootRect = container.getBoundingClientRect();
        const sY = Math.max(0, (rect.top - rootRect.top - pT) * 2);
        const sH = (rect.height + pT + pB) * 2;
        
        const fragCanvas = document.createElement('canvas');
        fragCanvas.width = bigCanvasWidth;
        fragCanvas.height = sH;
        const ctx = fragCanvas.getContext('2d');
        if (ctx) ctx.drawImage(bigCanvas, 0, sY, bigCanvasWidth, sH, 0, 0, bigCanvasWidth, sH);
        
        return {
          imgData: fragCanvas.toDataURL('image/jpeg', 0.7),
          heightMm: ((rect.height + pT + pB) * usableWidth) / rootRect.width
        };
      };

      const header = getFragment('#pdf-header', 0, 0);
      const footer = getFragment('#pdf-footer', 4, 8);

      const addHeaderOnly = () => {
        if (header.imgData) pdf.addImage(header.imgData, 'JPEG', margin, margin, usableWidth, header.heightMm, undefined, 'FAST');
      };

      const sectionIds = ['#pdf-section-header', '#pdf-section-1', '#pdf-section-2', '#pdf-signature'];
      let currentY = margin + header.heightMm + 10;
      addHeaderOnly();

      for (const id of sectionIds) {
        const frag = getFragment(id);
        if (!frag.imgData) continue;

        if (currentY + frag.heightMm > pdfHeight - margin - 20) {
          pdf.addPage();
          addHeaderOnly();
          currentY = margin + header.heightMm + 10;
        }
        pdf.addImage(frag.imgData, 'JPEG', margin, currentY, usableWidth, frag.heightMm, undefined, 'FAST');
        currentY += frag.heightMm + 8;
      }

      if (footer.imgData) {
        pdf.addImage(footer.imgData, 'JPEG', margin, pdfHeight - 8 - footer.heightMm, usableWidth, footer.heightMm, undefined, 'FAST');
      }

      const total = pdf.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(170);
        pdf.text(`Página ${i} de ${total}`, pdfWidth - margin - 18, pdfHeight - 5);
      }

      const nombre = opts.visita.productor
        ? `${opts.visita.productor.nombre}_${opts.visita.productor.primer_apellido}`
        : `visita_${opts.visita.id_visita}`;
      const nombreArchivo = `resumen_visita_${nombre}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`;
      const blob = pdf.output('blob') as Blob;
      await descargarOCompartirBlob(blob, nombreArchivo, this.toastService);

    } finally {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      document.body.removeChild(hostElement);
    }
  }

  private async waitForStableDom(componentRef: any): Promise<void> {
    const zone = this.appRef.injector.get(NgZone);
    await zone.run(async () => {
      componentRef.changeDetectorRef.detectChanges();
      await new Promise(r => setTimeout(r, 400));
      componentRef.changeDetectorRef.detectChanges();
    });
    const images = Array.from(componentRef.location.nativeElement.querySelectorAll('img')) as HTMLImageElement[];
    await Promise.all(images.map(img => img.complete ? Promise.resolve() : new Promise(r => { img.onload = r; img.onerror = r; })));
  }
}
